/**
 * This module provides general utility functions.
 * @module
 */

import type {FeedbackMessage, QueryParameters, UUID} from "../ui_general/GeneralInterfaces";
import {FeedbackMessageTypes, HTMLFormattedTextTypes, IDTypes, LabelTextMessageTypes, UserLanguages} from "../Settings";
import {LabelTextMessages} from "../LabelTextMessages";
import React from "react";
import {HTMLFormattedTexts} from "../HTMLFormattedTexts";


/**
 * This routine returns the current query string of the active url in the browser window.
 * @returns The current query string of the active url in the browser window. Encoded using encodeURI.
 */
export function getQueryString(): string {
    return encodeURI(window.location.search);
}


/**
 * This routine returns the URL path of the active url in the browser window.
 * @returns The URL path of the active url in the browser window. Encoded using encodeURI.
 */
export function getURLPath(): string {
    return encodeURI(window.location.pathname);
}


/**
 * This routine parses the query-string provided in the URL bar in the browser and searches for the specified parameter.
 * It extracts and returns its value as a string. If no such parameter is provided, undefined is returned.
 * @param parameter_name Name of parameter to search for in query-string.
 * @returns Value of parameter as string, encoded using encodeURI. If parameter not available, it returns undefined.
 */
export function getQueryStringParameter(parameter_name: string): string | undefined {
    const query_string = getQueryString();
    const params = new URLSearchParams(query_string);
    const value_str: null | string = params.get(parameter_name);
    if (value_str === null) return undefined;
    return encodeURI(value_str);
}


/**
 * This routine parses the query-string provided in the URL bar in the browser and searches for the specified parameter.
 * It extracts its value and converts it to a number. If no such parameter is provided or the conversion fails, the
 * return value is undefined.
 * @param parameter_name Name of parameter to search for in query-string.
 * @returns Value of parameter as number. If parameter not available or conversion fails, it returns undefined.
 */
export function getQueryStringParameterAsNumber(parameter_name: string): number | undefined {
    const value_str = getQueryStringParameter(parameter_name);
    if (value_str === undefined) return undefined;
    const value = Number(value_str);
    if (isNaN(value)) return undefined;
    return value;
}


/**
 * This function parses a string representation of a UUID of version 8 and returns the UUID as byte array.
 * If the parsing is unsuccessful or the given UUID is not of version 8 and variant 8, an exception is thrown.
 * @param uuid String representation of UUID to be parsed
 * @returns The created UUID byte array
 */
export function uuidParse(uuid: UUID): Uint8Array {
    const hexString = uuid.replace(/-/g, ""); // Remove dashes from the UUID string
    const uuidBytes: Array<number> = [];
    for (let c = 0; c < hexString.length; c += 2) uuidBytes.push(parseInt(hexString.substring(c, c + 2), 16));

    // Check the length of the byte array
    if (uuidBytes.length !== 16) throw new Error("Invalid UUID string");

    // Check the version
    const version = parseInt(uuid.slice(14, 15), 16);
    if (version !== 8) new Error("UUID has not version 8");

    // Check the variant bits
    const variant = (uuidBytes[8] & 0xc0) >> 4;
    if (variant !== 8) new Error("UUID has not variant 8");

    return Uint8Array.from(uuidBytes);
  }


/**
 * Convenience function for calling uuidParse and catching any exception. If an exception is caught, i.e. if the
 * given string representation does not reflect a version 8 UUID with variant 8, false is returned, otherwise true.
 * @param uuid String representation of UUID to be parsed
 * @returns True, if given string representation reflects a version 8 variant 8 UUID, otherwise false.
 */
export function uuidValidate(uuid: UUID): boolean {
    try {
        uuidParse(uuid);
        return true;
    } catch (error) {
        return false;
    }
}


/**
 * This routine parses the query-string provided in the URL bar in the browser and searches for the specified parameter.
 * It extracts its value and converts it to a UUID. If no such parameter is provided or the conversion fails, the
 * return value is undefined.
 * @param id_type Name of parameter to search for in query-string.
 * @returns Value of parameter as UUID. If parameter not available or conversion fails, it returns undefined.
 */
export function getQueryStringParameterAsUUID(id_type: IDTypes): UUID | undefined {
    const value_str = getQueryStringParameter(id_type);
    if (value_str === undefined) return undefined;
    if (!uuidValidate(value_str)) return undefined;
    return value_str;
}


/**
 * Transform a QueryParameters object into a string/string record by converting the values in the query parameters,
 * which are not strings yet, into strings.
 * @param query_parameters The QueryParameters object to be converted.
 * @returns The converted object as Record<string, string>
 */
export function transformQueryParametersToStringRecord(query_parameters: QueryParameters): Record<string, string> {
    const result: Record<string, string> = {};

    for (const key in query_parameters) {
        if (typeof query_parameters[key] === 'number') {
            result[key] = query_parameters[key].toString();
        } else {
            result[key] = query_parameters[key] as string;
        }
    }

    return result;
}


/**
 * This routine builds urls for accessing the api server.
 * @param api_endpoint This is the endpoint of the api route without server address, e.g. /api/transaction
 * @param query_parameters Specifies the parameters that should be incorporated into the url as query string
 * parameters.
 * @returns Constructed URL as string.
 */
export const buildApiUrl = (api_endpoint: string, query_parameters: QueryParameters): string => {
    const url = new URL(import.meta.env.VITE_BACKEND_URL + api_endpoint);

    url.search = new URLSearchParams(transformQueryParametersToStringRecord(query_parameters)).toString();

    return url.toString();
};


/**
 * This truncates a given string after a certain number of characters, in case the original string is longer. In that
 * case, three dots are also added at the end of the truncated string to notify about truncation.
 * @param txt Original string to be truncated if too long.
 * @param max_length Maximum length acceptable for truncated string.
 * @returns Truncated string or original string if already short enough.
 */
export function truncateString(txt: string, max_length: number): string {
    return txt.length > max_length ? txt.substring(0, max_length - 3) + '...' : txt;
}


/**
 * This truncates a given string, in case the original string is longer than the specified maximal string length. In
 * that case, three dots are inserted into the middle of the string and the respective number of characters are removed
 * from the middle of the string.
 * @param txt Original string to be truncated if too long.
 * @param max_length Maximum length acceptable for truncated string.
 * @returns Truncated string or original string if already short enough.
 */
export function truncateStringInMiddle(txt: string, max_length: number): string {
    return txt.length > max_length ? txt.substring(0, max_length / 2 - 2) + '...' +
        txt.substring(txt.length - max_length / 2 + 1, txt.length) : txt;
}


/**
 * This routine performs a deep copy of the given object and returns the copy. It works only for objects composed solely
 * of JSON-stringify-able data types.
 * @param obj Object to be copied. Can be of any type that is JSON-stringify-able-
 * @returns Returns the copied object.
 */
export function jsonDeepCopy(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
}


/**
 * This is a helper function that can be used in combination with filter to reduce an array to its unique values.
 * @param value A generic value of any type of element contained in an array.
 * @param index Index of the element.
 * @param self The array upon which the filter is acting.
 * @returns Boolean that indicates whether the occurrence of the value is the first in the array.
 */
export function onlyUnique(value: any, index: number, self: Array<any>): boolean {
  return self.indexOf(value) === index;
}


/**
 * Converts an input string into a technical name. A technical name only consists of lower case letters, has no special
 * characters and no spaces. The provided raw string is converted such that these requirements are met.
 * @param input_str Raw input string to be converted.
 * @returns Clean technical name (small letters, no special characters, no spaces)
 */
export function convert_string_to_technical_name(input_str: string): string {
    const regex = /\W+/g;
    return input_str.toLowerCase().replace(
        / /g, "_"
    ).replace(/:/g, "_").replace(regex, '');
}


/**
 * Helper function for processing enumerations conveniently. Returns an array holding the keys of the provided
 * enumeration which then allows to conveniently loop over these keys by means of the following code example:
 * for (const token_key of enumKeys(TokenTypes)) { TokenTypes[token_key] = ... }
 * @param enumeration Enumeration the keys of which shall be extracted and returned
 * @returns Array of the keys of the given enumeration
 */
export function enumKeys<O extends object, K extends keyof O = keyof O>(enumeration: O): K[] {
    return Object.keys(enumeration).filter(k => !Number.isNaN(k)) as K[]
}


/**
 * Retrieves the user language setting from local storage under the keyword 'user_language'. If no setting can be
 * found, the English language is returned as default.
 */
export function getUserLanguage(): UserLanguages {
    const local_storage_user_language = localStorage.getItem('user_language');
    if (!local_storage_user_language) return UserLanguages.EN;
    return local_storage_user_language as UserLanguages;
}


/**
 * Sets the user language in the browser's local storage under the keyword 'user_language'.
 * @param user_language User language setting to be stored in the browser's local storage.
 */
export function setUserLanguage(user_language: UserLanguages) {
    localStorage.setItem('user_language', user_language);
}


/**
 * Retrieves a label, text, or message as indicated by the message type in the language requested by the user language
 * parameter. The message can be further enriched by providing additional arguments to this function (rest arguments).
 * This, however, requires that the corresponding message text incorporates template expressions such as ${args[0]} or
 * ${args[1]}, etc...
 * @param message_type Type of message text being requested.
 * @param user_language Requested language.
 * @param _args Arbitrary number of additional arguments to be incorporated into the message string.
 * @returns The constructed message string in the desired language.
 */
export function getLabelTextMessage(message_type: LabelTextMessageTypes,
                                    user_language: UserLanguages,
                                    ..._args: Array<string | number>): string {
    return LabelTextMessages[message_type][user_language]();
}


/**
 * Retrieves a html-formatted text as indicated by the text type in the language requested by the user language
 * parameter.
 * @param text_type Type of text being requested.
 * @param user_language Requested language.
 * @returns The html-formatted text in the desired language.
 */
export function getHTMLFormattedText(text_type: HTMLFormattedTextTypes,
                                     user_language: UserLanguages): React.JSX.Element {
    return HTMLFormattedTexts[text_type][user_language];
}


/**
 * Creates a FeedbackMessage object of type ERROR with specified http-code, if provided. If omitted, http-code 400 is
 * set. The message text is set according to the provided message type.
 * @param type Message type specifying the text of the error message.
 * @param http_code Optional HTTP code.
 */
export function createErrorFeedbackMessage(type: LabelTextMessageTypes, http_code?: number): FeedbackMessage {
    const feedback_message: FeedbackMessage = {
        message_type: FeedbackMessageTypes.ERROR,
        message_text: getLabelTextMessage(
            type,
            getUserLanguage()
        ),
    }
    feedback_message.http_code = http_code ? http_code : 400;
    return feedback_message;
}


/**
 * Creates a FeedbackMessage object of type ERROR with specified http-code, if provided. If omitted, http-code 400 is
 * set. The message text is set according to the provided message type.
 * @param type Message type specifying the text of the error message.
 */
export function createSuccessFeedbackMessage(type: LabelTextMessageTypes): FeedbackMessage {
    const feedback_message: FeedbackMessage = {
        message_type: FeedbackMessageTypes.SUCCESS,
        message_text: getLabelTextMessage(
            type,
            getUserLanguage()
        ),
    }
    return feedback_message;
}


/**
 * Transforms the given UTC timestamp provided as string and referring to UTC timezone to a JavaScript Date object
 * carrying the UTC timezone information. String representations of dates and times can then be obtained from the date
 * object using the standard routines, such as toLocaleString(), toLocaleDateString(), toLocaleTimeString(), etc.
 * @param utc_timestamp_string String holding the UTC timestamp.
 * @returns JavaScript Date object holding the utc timestamp with utc timezone information.
 */
export function digestUTCTimestamp(utc_timestamp_string: string): Date {
    const raw_date = new Date(utc_timestamp_string);
    return new Date(Date.UTC(
        raw_date.getFullYear(),
        raw_date.getMonth(),
        raw_date.getDate(),
        raw_date.getHours(),
        raw_date.getMinutes(),
        raw_date.getSeconds()
    ));
}


/**
 * Converts a given file size in bytes into a readable string presentation.
 * @param file_size File size in bytes.
 * @returns String representation of file size in readable form.
 */
export function readableFileSize(file_size: number) {
    const file_size_strings = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
    let result = 'EXTREMELY LARGE';
    file_size_strings.forEach((fs, ind) => {
        const scale = 1024**ind;
        if (file_size >= scale) result = (Math.round(10 * file_size / scale) / 10).toString() + ' ' + fs;
    })
    return result;
}


/**
 * Formats a JavaScript Date object as a string in the format YYYY-MM-DD (local time).
 * @param date JavaScript Date object to format.
 * @returns Date string in YYYY-MM-DD format (local time).
 */
export function formatDateToStringInLocalTZ(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Calculates the start date of the next quarter based on the provided date.
 * @param date JavaScript Date object representing the reference date.
 * @returns JavaScript Date object representing the start of the next quarter.
 */
export function getNextQuarterStart(date: Date): Date {
    const currentMonth = date.getMonth(); // 0-11
    const currentYear = date.getFullYear();
    const nextQuarterStartMonth = currentMonth < 3
        ? 3
        : currentMonth < 6
            ? 6
            : currentMonth < 9
                ? 9
                : 0;
    const year = currentMonth >= 9 ? currentYear + 1 : currentYear;
    return new Date(year, nextQuarterStartMonth, 1);
}

/**
 * Calculates the end date of the next quarter based on the provided date.
 * @param date JavaScript Date object representing the reference date.
 * @returns JavaScript Date object representing the end of the next quarter.
 */
export function getNextQuarterEnd(date: Date): Date {
    const currentMonth = date.getMonth();
    const currentYear = date.getFullYear();
    const nextQuarterEndMonth = currentMonth < 3
        ? 5
        : currentMonth < 6
            ? 8
            : currentMonth < 9
                ? 11
                : 2;
    const year = currentMonth >= 9 ? currentYear + 1 : currentYear;
    return new Date(year, nextQuarterEndMonth + 1, 0);
}

/**
 * Calculates the height from the top of the referenced object to the bottom of the window minus the given offset.
 * Useful for determining the remaining vertical space for scrollable containers.
 * @param windowInnerHeight The current inner height of the browser window.
 * @param offset The offset in pixels to subtract from the available space.
 * @param objectRef A React ref to the HTMLDivElement whose top position is used for calculation.
 * @returns The calculated height in pixels (never negative).
 */
export function calcHeightToBottomOffset (
    windowInnerHeight: number,
    offset: number,
    objectRef: React.RefObject<HTMLDivElement> | null
): number | undefined {
    if (objectRef?.current) {
        const rect = objectRef.current.getBoundingClientRect();
        const height = windowInnerHeight - rect.top - offset;
        return height > 0 ? height : 0;
    }
    return undefined;
}

/**
 * Formats a number as a Euro currency string with two decimal places and a trailing Euro sign.
 * Returns '-' if the value is not a valid number.
 * @param value The number to format.
 * @returns Formatted Euro string or '-'.
 */
export function formatEuro(value: number | undefined): string {
    if (typeof value !== "number" || isNaN(value)) return "-";
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Formats a number as a percentage string with two decimal places and a trailing percent sign.
 * Returns '-' if the value is not a valid number.
 * @param value The number to format.
 * @returns Formatted percent string or '-'.
 */
export function formatPercent(value: number | undefined): string {
    if (typeof value !== "number" || isNaN(value)) return "---";
    value = value * 100.0
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' %';
}


/**
* Indicates whether the frontend is running in debugging mode.
* Debug mode is enabled when the environment variable VITE_DEBUGGING is set to the string 'true'.
* In this mode, certain development conveniences may be enabled (e.g. skipping authentication or
* reducing logging requirements). This flag must never be relied upon for security or authorization
* decisions on the backend.
* @returns {boolean} true if debugging mode is active; false otherwise. */
export function isDebug(): boolean {
    return import.meta.env.VITE_DEBUGGING === 'true';
}

